/* ============================================================
   The provider tree and the router. The order is the client core's (see
   client-core/README.md): the core's root, auth, preferences, then this front
   end's theme, then the gates — sign-in, entitlement, the hydrated store.

   Two routes sit OUTSIDE every provider: the OAuth popup's landing pages.
   ============================================================ */
import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import {
  AuthProvider,
  ClientCoreProvider,
  EntitlementProvider,
  PreferencesProvider,
  StoreProvider,
  useAuth,
  useEntitlement,
} from '@habishine/client-core';

import { screen } from './analytics';
import { CallbackPage, SignoutPage } from './auth/CallbackPage';
import { SignInPage } from './auth/SignInPage';
import { Spinner } from './components/ui';
import { clientConfig } from './config';
import { HabitsPage } from './pages/HabitsPage';
import { PaywallGate } from './pages/PaywallPage';
import { PlanPage } from './pages/PlanPage';
import { ReviewPage } from './pages/ReviewPage';
import { ScopePage } from './pages/ScopePage';
import { SettingsPage } from './pages/SettingsPage';
import { TodayPage } from './pages/TodayPage';
import { adapters } from './platform';
import { CALLBACK_PATH, SIGNOUT_PATH } from './platform/oauth';
import { AppShell } from './shell/AppShell';
import { ThemeProvider } from './theme/ThemeProvider';

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path={CALLBACK_PATH} element={<CallbackPage />} />
        <Route path={SIGNOUT_PATH} element={<SignoutPage />} />
        <Route path="/*" element={<Providers />} />
      </Routes>
    </BrowserRouter>
  );
}

function Providers() {
  return (
    <ClientCoreProvider config={clientConfig} adapters={adapters}>
      <AuthProvider>
        <PreferencesProvider>
          <ThemeProvider>
            <Gate />
          </ThemeProvider>
        </PreferencesProvider>
      </AuthProvider>
    </ClientCoreProvider>
  );
}

function Gate() {
  const { status } = useAuth();
  // a dev-only window onto the signed-out scene, so it can be reviewed without signing out
  if (import.meta.env.DEV && window.location.pathname === '/preview/signin') return <SignInPage />;
  if (status === 'loading') return <Spinner label="Restoring your session…" />;
  if (status === 'signedOut') return <SignInPage />;
  return (
    <EntitlementProvider>
      <EntitlementGate />
    </EntitlementProvider>
  );
}

function EntitlementGate() {
  const { loading, hardPaywall } = useEntitlement();
  if (loading) return <Spinner />;
  if (hardPaywall) return <PaywallGate />;
  return (
    <StoreProvider fallback={<Spinner label="Loading your day…" />}>
      <ScreenTracker />
      <Routes>
        <Route element={<AppShell />}>
          <Route path="today" element={<TodayPage />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="review" element={<ReviewPage />} />
          <Route path="habits" element={<HabitsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="project/:id" element={<ScopePage kind="project" />} />
          <Route path="tag/:id" element={<ScopePage kind="tag" />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Route>
      </Routes>
    </StoreProvider>
  );
}

/** Reports the route to analytics as a screen, under the same names the phone
    uses, so `today` here and `today` there are one row. A route NAME, never
    an id: `/project/<uuid>` is `project`. Sits inside the entitlement gate, so
    sign-in and the paywall report themselves (SignInPage, PaywallPage). */
function ScreenTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    const name = pathname.split('/').filter(Boolean)[0] ?? 'today';
    screen(name);
  }, [pathname]);
  return null;
}

export default React.memo(App);
